const lightColors = {
  primary: '#0F9C8C',
  primaryDark: '#0B7A6E',
  primaryLight: '#5EEAD4',
  accent: '#E24E72',
  violet: '#DED5FF',
  danger: '#DC2626',
  warning: '#D97706',
  success: '#16A34A',
  background: '#F8F7FC',
  card: '#FFFFFF',
  border: '#E5E1F2',
  text: '#1E1B2E',
  textMuted: '#726B93',
  textFaint: '#A79CD6',
  primarySoft: '#E6FBF6',
};

// "Mocha" — warm cream/tan/deep-brown palette with a navy accent, inspired by
// the iOS default wallpaper gradient. A third standalone look alongside
// light/dark, not a variant of either — same light-background structure as
// lightColors (so functional colors like danger/warning/success are reused
// as-is) but with every brand-facing color swapped to the warm palette.
const mochaColors = {
  primary: '#8A6438',
  primaryDark: '#5E4527',
  primaryLight: '#C9A876',
  accent: '#33415F',
  violet: '#D7DCE8',
  danger: '#DC2626',
  warning: '#D97706',
  success: '#16A34A',
  background: '#F6F0E6',
  card: '#FFFFFF',
  border: '#E4D9C4',
  text: '#2B2318',
  textMuted: '#8A7D6B',
  textFaint: '#B8AC97',
  primarySoft: '#F1E6D2',
};

const darkColors = {
  primary: '#14B8A6',
  primaryDark: '#0F9C8C',
  primaryLight: '#5EEAD4',
  accent: '#E24E72',
  violet: '#DED5FF',
  danger: '#F87171',
  warning: '#FBBF24',
  success: '#4ADE80',
  background: '#16112B',
  card: '#221B42',
  border: '#362A5E',
  text: '#F3F1FF',
  textMuted: '#A79CD6',
  textFaint: '#7C6FA8',
  primarySoft: '#1B3B36',
};

const lightGradients = {
  primary: ['#0F9C8C', '#E24E72'] as [string, string],
  hero: ['#0F9C8C', '#E24E72'] as [string, string],
  wallet: ['#0F9C8C', '#E24E72'] as [string, string],
  success: ['#16A34A', '#22C55E'] as [string, string],
  soft: ['#E6FBF6', '#EDE9FF'] as [string, string],
};

const darkGradients = {
  primary: ['#14B8A6', '#E24E72'] as [string, string],
  hero: ['#14B8A6', '#E24E72'] as [string, string],
  wallet: ['#14B8A6', '#E24E72'] as [string, string],
  success: ['#16A34A', '#4ADE80'] as [string, string],
  soft: ['#1B3B36', '#2A2050'] as [string, string],
};

const mochaGradients = {
  primary: ['#C9A876', '#33415F'] as [string, string],
  hero: ['#C9A876', '#33415F'] as [string, string],
  wallet: ['#C9A876', '#33415F'] as [string, string],
  success: ['#16A34A', '#22C55E'] as [string, string],
  soft: ['#F6F0E6', '#EDE0C8'] as [string, string],
};

export type ThemeMode = 'light' | 'dark' | 'mocha';

const colorsByMode: Record<ThemeMode, typeof lightColors> = {
  light: lightColors,
  dark: darkColors,
  mocha: mochaColors,
};

const gradientsByMode: Record<ThemeMode, typeof lightGradients> = {
  light: lightGradients,
  dark: darkGradients,
  mocha: mochaGradients,
};

// Mutated in place (not reassigned) so screens that did `import { colors }`
// at module load time keep seeing live values after a theme toggle, as long
// as something up the tree forces those screens to re-render.
export const colors = { ...lightColors };
export const gradients = { ...lightGradients };

export let themeMode: ThemeMode = 'light';

export function applyThemeMode(mode: ThemeMode) {
  themeMode = mode;
  Object.assign(colors, colorsByMode[mode]);
  Object.assign(gradients, gradientsByMode[mode]);
}

export const radii = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 22,
  pill: 999,
};

export const shadows = {
  card: {
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 2,
  },
  button: {
    shadowColor: '#0F9C8C',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 4,
  },
};

// Web/PWA builds often can't read the real safe-area inset reliably (Safari
// standalone mode in particular reports 0 or updates it a frame late), so
// every screen falls back to these fixed minimums instead of trusting 0 —
// sized to clear a notch/Dynamic Island (top) and home indicator (bottom) on
// the tallest common phones, even if that slightly overshoots on others.
export const minSafeArea = {
  top: 50,
  bottom: 24,
};

export const fonts = {
  regular: 'Inter_400Regular',
  medium: 'Inter_500Medium',
  semiBold: 'Inter_600SemiBold',
  bold: 'Inter_600SemiBold',
  extraBold: 'Inter_700Bold',
};
